import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2,
  X,
  ShoppingCart,
  ChefHat,
  UtensilsCrossed,
  Droplets,
  Plane,
  PartyPopper,
  Heart,
  Sparkles,
  Check,
} from "lucide-react";
import request from "../../utils/request";
import PhoneDemo from "../PhoneDemo";
import { DEMO_SERVICES } from "../demoServices";

const DISMISS_KEY = "concierge_promo_dismissed";
const PHONE_SCALE = 190 / PhoneDemo.WIDTH;

/* ─── PhoneDemo Design Tokens (shared visual language) ─── */
const DS = {
  bg: "#ffffff",
  card: "#F7F7F8",
  cardBorder: "#EAEAEA",
  textPrimary: "#111111",
  textSecondary: "#5F6368",
  textMuted: "#888888",
  btnPrimary: "#111111",
  success: "#10b981",
  divider: "rgba(0,0,0,0.06)",
};

function getTieredPrice(count) {
  if (count <= 0) return null;
  if (count <= 2) return 50;
  if (count <= 4) return 75;
  return 100;
}

const ICON_MAP = {
  ShoppingCart,
  ChefHat,
  UtensilsCrossed,
  Droplets,
  Plane,
  PartyPopper,
  Heart,
  Sparkles,
};

const CONCIERGE_SERVICES = DEMO_SERVICES.filter((s) => s.tier === "concierge");

/* ─── A/B Copy Variants ─── */

const COPY_VARIANTS = {
  A: {
    headline: "Groceries are on us.\nConcierge is up to you.",
    subheadline:
      "Pre-arrival grocery delivery is already included with every property. Toggle on premium services to elevate every stay.",
    includedLabel: "Included with Bellhopt",
    includedSubtext: "Always available to your guests — no extra cost",
    optionalLabel: "Additional concierge services",
    cta: "Enable Concierge",
  },
  B: {
    headline: "Offer more than\njust groceries",
    subheadline:
      "Grocery delivery comes standard. Unlock private chefs, spa, transport, and more to turn stays into experiences.",
    includedLabel: "Already active",
    includedSubtext: "Your guests can order groceries right now",
    optionalLabel: "Unlock premium services",
    cta: "Get Started",
  },
  C: {
    headline: "Try premium guest\nservices — no commitment",
    subheadline:
      "Groceries are already covered. Add concierge services one at a time, only pay for what you enable.",
    includedLabel: "Free — always on",
    includedSubtext: "Grocery delivery is part of every Bellhopt property",
    optionalLabel: "Add services as you go",
    cta: "Enable Concierge",
  },
  D: {
    headline: "Build your guest\nexperience, service by service",
    subheadline:
      "Grocery delivery is built in. Pick exactly which concierge services you want to offer — nothing more, nothing less.",
    includedLabel: "Built in",
    includedSubtext: "Active on all your properties automatically",
    optionalLabel: "Choose your services",
    cta: "Activate Services",
  },
  E: {
    headline: "Elevate your property\nto five-star",
    subheadline:
      "Guests already get grocery delivery. Add the concierge layer that separates good stays from unforgettable ones.",
    includedLabel: "Standard — included",
    includedSubtext: "Pre-arrival groceries, every booking",
    optionalLabel: "The concierge layer",
    cta: "Upgrade Now",
  },
};

const ACTIVE_VARIANT = "B";

/* ─── Toggle Switch ─── */

function Switch({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        width: 40,
        height: 22,
        borderRadius: 11,
        border: "none",
        cursor: "pointer",
        padding: 0,
        background: checked ? DS.btnPrimary : DS.cardBorder,
        transition: "background 0.2s ease",
        flexShrink: 0,
      }}
    >
      <motion.span
        animate={{ x: checked ? 20 : 2 }}
        transition={{ type: "spring", stiffness: 500, damping: 30 }}
        style={{
          display: "block",
          width: 18,
          height: 18,
          borderRadius: 9,
          background: DS.bg,
          boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
        }}
      />
    </button>
  );
}

/* ─── Service Toggle Row ─── */

function ServiceToggle({ service, active, onToggle, isLast }) {
  const Icon = ICON_MAP[service.icon] || Sparkles;
  return (
    <motion.div
      layout
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 0",
        borderBottom: isLast ? "none" : `1px solid ${DS.divider}`,
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 10,
          background: active ? "rgba(16,185,129,0.08)" : DS.bg,
          border: `1px solid ${active ? "rgba(16,185,129,0.2)" : DS.cardBorder}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          transition: "all 0.2s ease",
        }}
      >
        <Icon
          size={16}
          color={active ? DS.success : DS.textMuted}
          strokeWidth={1.8}
          style={{ transition: "color 0.2s ease" }}
        />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            color: active ? DS.textPrimary : DS.textSecondary,
            lineHeight: 1.3,
            transition: "color 0.2s ease",
          }}
        >
          {service.toggleLabel}
        </div>
        <div
          style={{
            fontSize: 12,
            color: DS.textMuted,
            lineHeight: 1.3,
            marginTop: 2,
          }}
        >
          {service.benefit}
        </div>
      </div>
      <Switch checked={active} onChange={() => onToggle(service.id)} />
    </motion.div>
  );
}

/* ─── Main Component ─── */

export default function ConciergePromoCard({ hostId }) {
  const navigate = useNavigate();
  const copy = COPY_VARIANTS[ACTIVE_VARIANT];

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === "true"
  );

  const [toggleState, setToggleState] = useState(() => {
    const initial = {};
    CONCIERGE_SERVICES.forEach((s) => {
      initial[s.id] = false;
    });
    return initial;
  });

  const enabledServices = useMemo(() => {
    const set = new Set(["grocery-delivery"]);
    Object.entries(toggleState).forEach(([id, on]) => {
      if (on) set.add(id);
    });
    return set;
  }, [toggleState]);

  const enabledCount = useMemo(
    () => Object.values(toggleState).filter(Boolean).length,
    [toggleState]
  );

  const handleToggle = useCallback((serviceId) => {
    setToggleState((prev) => ({ ...prev, [serviceId]: !prev[serviceId] }));
  }, []);

  useEffect(() => {
    if (!hostId || dismissed) return;
    request
      .get(`/hosts/${hostId}/concierge/status`)
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ conciergeEnabled: false, status: "none" }))
      .finally(() => setLoading(false));
  }, [hostId, dismissed]);

  const handleSubscribe = async () => {
    setActionLoading(true);
    try {
      const { data } = await request.post(
        `/hosts/${hostId}/concierge/subscribe`,
        { tier: "basic" }
      );
      if (data.url) window.location.href = data.url;
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to start checkout");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, "true");
    setDismissed(true);
  };

  if (dismissed || loading || status?.conciergeEnabled) return null;

  const plan = status?.plans?.basic;
  const basePriceLabel = plan ? `$${(plan.priceInCents / 100).toFixed(0)}` : "$100";
  const tieredPrice = getTieredPrice(enabledCount);
  const priceLabel = tieredPrice ? `$${tieredPrice}` : basePriceLabel;

  return (
    <div
      style={{
        position: "relative",
        background: DS.bg,
        border: `1px solid ${DS.cardBorder}`,
        borderRadius: 24,
        overflow: "hidden",
      }}
    >
      {/* Dismiss */}
      <button
        onClick={handleDismiss}
        style={{
          position: "absolute",
          top: 20,
          right: 20,
          zIndex: 10,
          width: 32,
          height: 32,
          borderRadius: 16,
          border: `1px solid ${DS.cardBorder}`,
          background: DS.card,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        aria-label="Dismiss"
      >
        <X size={14} color={DS.textMuted} />
      </button>

      {/* ─── Hero Banner ─── */}
      <div
        style={{
          padding: "40px 32px 32px",
          borderBottom: `1px solid ${DS.cardBorder}`,
        }}
      >
        <h2
          style={{
            fontSize: 28,
            fontWeight: 800,
            letterSpacing: -0.5,
            lineHeight: 1.1,
            color: DS.textPrimary,
            margin: "0 0 6px 0",
          }}
        >
          Offer More To Your Guests
        </h2>

        <p
          style={{
            fontSize: 15,
            fontWeight: 500,
            color: DS.textSecondary,
            margin: "0 0 24px 0",
          }}
        >
          Add more amenities to your properties
        </p>

        <style>{`
          @keyframes conciergeShimmer {
            0% { background-position: -200% center; }
            100% { background-position: 200% center; }
          }
        `}</style>

        <h1
          style={{
            fontSize: 34,
            lineHeight: 1.05,
            fontWeight: 800,
            letterSpacing: -0.5,
            color: DS.textPrimary,
            margin: "0 0 12px 0",
          }}
        >
          Pre-Arrival Grocery Delivery is Free.
          <br />
          <span
            style={{
              background:
                "linear-gradient(105deg, #c0392b 0%, #e74c3c 20%, #ff6b6b 35%, #ffb3b3 50%, #ff6b6b 65%, #e74c3c 80%, #c0392b 100%)",
              backgroundSize: "200% auto",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              animation: "conciergeShimmer 3s ease-in-out infinite",
            }}
          >
            Unlock More Amenities for Your Guests.
          </span>
        </h1>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "stretch",
        }}
      >
        {/* ─── Left Panel: Configurator ─── */}
        <div
          style={{
            flex: 1,
            padding: 32,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
          }}
        >

          {/* ─── Section A: Included ─── */}
          <div
            style={{
              background: "rgba(16,185,129,0.06)",
              border: "1px solid rgba(16,185,129,0.15)",
              borderRadius: 16,
              padding: 16,
              marginBottom: 24,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: "rgba(16,185,129,0.1)",
                  border: "1px solid rgba(16,185,129,0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <ShoppingCart size={18} color={DS.success} strokeWidth={1.8} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontSize: 16,
                      fontWeight: 600,
                      color: DS.textPrimary,
                    }}
                  >
                    Pre-arrival Groceries
                  </span>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                      padding: "3px 10px",
                      borderRadius: 12,
                      background: DS.success,
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#fff",
                    }}
                  >
                    <Check size={10} strokeWidth={3} />
                    {copy.includedLabel}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: DS.textMuted,
                    marginTop: 4,
                    lineHeight: 1.4,
                  }}
                >
                  {copy.includedSubtext}
                </div>
              </div>
            </div>
          </div>

          {/* ─── Section B: Optional Services ─── */}
          <div style={{ flex: 1, minHeight: 0 }}>
            <div
              style={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                color: DS.textMuted,
                marginBottom: 8,
              }}
            >
              {copy.optionalLabel}
            </div>

            <div
              style={{
                background: DS.card,
                border: `1px solid ${DS.cardBorder}`,
                borderRadius: 16,
                padding: "4px 16px",
              }}
            >
              {CONCIERGE_SERVICES.map((svc, i) => (
                <ServiceToggle
                  key={svc.id}
                  service={svc}
                  active={toggleState[svc.id]}
                  onToggle={handleToggle}
                  isLast={i === CONCIERGE_SERVICES.length - 1}
                />
              ))}
            </div>
          </div>

          {/* ─── CTA Row ─── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginTop: 24,
              paddingTop: 20,
              borderTop: `1px solid ${DS.divider}`,
            }}
          >
            <button
              onClick={handleSubscribe}
              disabled={actionLoading}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "14px 24px",
                background: DS.btnPrimary,
                color: "#fff",
                fontSize: 15,
                fontWeight: 700,
                borderRadius: 12,
                border: "none",
                cursor: "pointer",
                opacity: actionLoading ? 0.6 : 1,
                transition: "opacity 0.15s",
              }}
            >
              {actionLoading && (
                <Loader2
                  size={14}
                  style={{ marginRight: 6 }}
                  className="animate-spin"
                />
              )}
              <span>{copy.cta} —&nbsp;</span>
              <AnimatePresence mode="wait">
                <motion.span
                  key={priceLabel}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.15 }}
                >
                  {priceLabel}/mo
                </motion.span>
              </AnimatePresence>
            </button>

            <button
              onClick={() => navigate("/concierge-subscription")}
              style={{
                padding: "14px 20px",
                background: "transparent",
                border: `1.5px solid ${DS.cardBorder}`,
                borderRadius: 12,
                fontSize: 15,
                fontWeight: 600,
                color: DS.textSecondary,
                cursor: "pointer",
              }}
            >
              Learn more
            </button>

            <AnimatePresence>
              {enabledCount > 0 && (
                <motion.span
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  style={{
                    fontSize: 12,
                    color: DS.success,
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    marginLeft: "auto",
                  }}
                >
                  {enabledCount} service{enabledCount !== 1 ? "s" : ""} selected
                </motion.span>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* ─── Right Panel: Phone Preview ─── */}
        <div
          style={{
            flexShrink: 0,
            width: 280,
            background: DS.card,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderLeft: `1px solid ${DS.cardBorder}`,
          }}
        >
          <div
            style={{
              width: 200,
              height: 408,
              borderRadius: 34,
              background: "#000",
              border: "1px solid rgba(0,0,0,0.1)",
              position: "relative",
              boxShadow:
                "0 20px 60px rgba(0,0,0,0.12), 0 8px 24px rgba(0,0,0,0.08)",
              overflow: "hidden",
            }}
          >
            {/* Dynamic Island */}
            <div
              style={{
                position: "absolute",
                top: 7,
                left: "50%",
                transform: "translateX(-50%)",
                width: 60,
                height: 18,
                borderRadius: 9,
                background: "#000",
                zIndex: 20,
              }}
            />
            {/* Screen */}
            <div
              style={{
                position: "absolute",
                top: 4,
                left: 4,
                right: 4,
                bottom: 4,
                borderRadius: 30,
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <div
                style={{
                  width: PhoneDemo.WIDTH,
                  height: PhoneDemo.HEIGHT,
                  transform: `scale(${PHONE_SCALE})`,
                  transformOrigin: "top left",
                }}
              >
                <PhoneDemo enabledServices={enabledServices} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
